import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { type ActionFunctionArgs, json, type MetaFunction } from '@remix-run/node'
import { Link, redirect, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorBulletList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { postAndReturnStatus } from '#app/utils/refundr.server.aragorn.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import React from 'react'
import { useTranslation } from 'react-i18next'
import i18next from '#app/i18next.server.ts'

export const meta: MetaFunction = () => {
	return [{ title: 'Forgot Password | refundr' }]
}

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const t = await i18next.getFixedT(request)
	const formData = await request.formData()
	// Honeypot is a simple technique to prevent spam bots from submitting forms. It works by adding a hidden field to the form that bots will fill, but humans won't.
	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const response = await postAndReturnStatus<{ email: string }>(
				request,
				'/aws/forgot-password/',
				{
					email: data.email,
				},
				true,
			)
			if (!response?.success && 400 === response?.statusCode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: response.data,
				})
				return z.NEVER
			}
			if (!response?.success && 403 === response?.statusCode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'FORBIDDEN_403',
				})
				return z.NEVER
			}
			return { ...data, response: response.data }
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		// TODO: this is f'd up
		let found = false
		// @ts-ignore
		const errorArray = submission.error['']
		if (Array.isArray(errorArray)) {
			for (const error of errorArray) {
				if (error.includes('FORBIDDEN_403')) {
					found = true
					break
				}
			}
		}

		if (found) {
			return redirectWithToast(`/verify-code?target=${formData.get('email')}`, {
				type: 'error',
				title: t('auth.forgotPassword.emailNotVerifiedTitle'),
				description: t('auth.forgotPassword.emailNotVerifiedDescription'),
			})
		}

		return json(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	return redirect('/reset-password')
}

export default function ForgotPasswordRoute() {
	const { t } = useTranslation()
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-screen flex-col md:flex-row">
			{/* Left: Marketing Section */}
			<div className="dark-background order-last flex w-full items-center justify-center border-neutral-200 p-6 text-black md:order-none md:w-1/2 md:border-r dark:border-neutral-800 dark:text-white">
				<div className="mt-5 flex flex-col items-center justify-center space-y-4">
					<div>
						<img className="block h-12 w-auto dark:hidden" src="/img/logo/logo-black.png" alt="refundr" />
						<img className="hidden h-12 w-auto dark:block" src="/img/logo/refundr-white.png" alt="refundr" />
					</div>
					<div className="text-center font-proxima text-neutral-600 dark:text-gray-200">{t('marketing.index.subtitle1')}</div>
					<div className="pt-4">
						<img className="block w-auto rounded-xl bg-white shadow-[0_12px_40px_rgba(15,24,42,0.18)] ring-1 ring-neutral-400/30" src="/img/tax-return-card-md.png" alt="refundr" />
					</div>
				</div>
			</div>

			{/* Right: Login Section */}
			<div className="flex w-full items-center justify-center bg-white p-6 md:w-1/2">
				<div className="mb-16 mt-5 w-full max-w-md bg-white">
					<div className="mb-6 flex items-center justify-center">
						<div className="text-2xl font-bold text-gray-600">{t('auth.forgotPassword.heading')}</div>
					</div>
					<div className="mb-6 flex items-center justify-center">
						<div className="mt-3 text-body-sm text-muted-foreground">{t('auth.forgotPassword.instructions')}</div>
					</div>
					<div>
						<forgotPassword.Form method="POST" {...getFormProps(form)}>
							<ErrorBulletList errors={form.errors} id={form.errorId} />
							<HoneypotInputs />
							<div>
								<Field
									labelProps={{
										htmlFor: fields.email.id,
										children: t('auth.forgotPassword.emailLabel'),
										className: 'text-gray-700',
									}}
									inputProps={{
										autoFocus: true,
										...getInputProps(fields.email, { type: 'email' }),
										className:
											'lowercase h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
										placeholder: t('auth.forgotPassword.emailPlaceholder'),
									}}
									errors={fields.email.errors}
								/>
							</div>
							<div className="mt-6">
								<StatusButton
									className="h-12 w-full rounded-md bg-neutral-900 dark:bg-blue-600 dark:hover:bg-blue-700 px-4 py-2 text-white hover:bg-neutral-800"
									status={forgotPassword.state === 'submitting' ? 'pending' : (form.status ?? 'idle')}
									type="submit"
									disabled={forgotPassword.state !== 'idle'}
								>
									{t('auth.forgotPassword.submit')}
								</StatusButton>
							</div>
						</forgotPassword.Form>
						<div className="mt-1">
							<Link to="/login" className="text-center text-body-sm font-bold text-gray-500">
								{t('auth.forgotPassword.backToLogin')}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
